import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

/* ================= TYPES ================= */
type PromotionCycle = {
  id: string;
  name: string;
  grade: string;
  cadré: string;
  baseDate: string;
  eligible: number;
  lists: {
    A: number;
    B: number;
    C: number;
    D: number;
    E: number;
  };
  status: "Draft" | "Active" | "DPC Review" | "Approved" | "SAP Synced" | "Archived";
};

type PromotionItem = {
  id?: string | number;
  PromotedEmployee: string;
  EmpNo?: string;
  DesignationFrom?: string;
  DesignationTo?: string;
  Department?: string;
  PromotionDate?: string;
  remarks?: string;
};

interface TableColumn<T> {
  title: string;
  key: string;
  className?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
  title,
  icon,
  count,
  link,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  colorClass?: string;
}) => {
  return (
    <Link to={link || "#"} className="text-decoration-none col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">

          <div
            className={`d-flex align-items-center justify-content-center mb-2 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>

          <h6 className="fw-bold text-dark mb-0 w-100 px-1 text-truncate" title={title} style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
            {title}
          </h6>

          {count !== undefined && (
            <div className="mt-2">
              <span className={`badge rounded-pill bg-light ${colorClass} border`} style={{ fontSize: "10px", padding: "3px 8px", fontWeight: "600" }}>
                {count} {count === 1 ? 'Item' : 'Items'}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

/* ================= MAIN ================= */
const PostPromotion: React.FC = () => {
  const routes = all_routes;
  // State for promotion cycles
  const [cycles, setCycles] = useState<PromotionCycle[]>([
    {
      id: "PC2025-001",
      name: " ",
      cadré: "Non-Executive",
      grade: "E2",
      baseDate: "12/31/2024",
      eligible: 24,
      lists: { A: 5, B: 3, C: 2, D: 1, E: 0 },
      status: "Active",
    },
    {
      id: "PC2025-002",
      name: "E1 to E2 Promotion - Q1 2025",
      cadré: "Non-Executive",
      grade: "E1",
      baseDate: "12/31/2024",
      eligible: 18,
      lists: { A: 2, B: 1, C: 1, D: 0, E: 0 },
      status: "DPC Review",
    },
    {
      id: "PC2025-003",
      name: "E3 to E4 Promotion - Q1 2025",
      cadré: "Executive",
      grade: "E3",
      baseDate: "12/31/2024",
      eligible: 15,
      lists: { A: 6, B: 4, C: 3, D: 2, E: 0 },
      status: "Approved",
    },
    {
      id: "PC2024-004",
      name: "E4 to E5 Promotion - Q4 2024",
      cadré: "Executive",
      grade: "E4",
      baseDate: "09/30/2024",
      eligible: 12,
      lists: { A: 3, B: 2, C: 1, D: 1, E: 5 },
      status: "SAP Synced",
    },
  ]);

  // Load initiated cycles list from localStorage and merge/override
  useEffect(() => {
    const stored = localStorage.getItem("promotion_cycles");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCycles(prev => {
            const merged = [...prev];
            parsed.forEach((newC: any) => {
              const existingIdx = merged.findIndex(c => c.id === newC.id || String(c.id) === String(newC.id));
              const formattedCycle: PromotionCycle = {
                id: String(newC.id),
                name: newC.name,
                cadré: newC.cadre || "Non-Executive",
                grade: newC.grade,
                baseDate: newC.baseDate,
                eligible: newC.eligible || 0,
                lists: newC.lists || { A: 0, B: 0, C: 0, D: 0, E: 0 },
                status: newC.status || "Active"
              };
              if (existingIdx !== -1) {
                merged[existingIdx] = formattedCycle;
              } else {
                merged.unshift(formattedCycle);
              }
            });
            return merged;
          });
        }
      } catch (e) {
        console.error("Failed to parse promotion cycles in dashboard", e);
      }
    }
  }, []);

  // State for promotion items (employee list)
  const [promotionItems, setPromotionItems] = useState<PromotionItem[]>([
    {
      id: 1,
      PromotedEmployee: "John Smith",
      EmpNo: "EMP00123",
      DesignationFrom: "Software Engineer",
      DesignationTo: "Senior Software Engineer",
      Department: "Technology",
      PromotionDate: "2025-01-15",
      remarks: "Outstanding performance in Q4",
    },
    {
      id: 2,
      PromotedEmployee: "Sarah Johnson",
      EmpNo: "EMP00456",
      DesignationFrom: "Marketing Executive",
      DesignationTo: "Marketing Manager",
      Department: "Marketing",
      PromotionDate: "2025-01-20",
      remarks: "Led successful campaign",
    },
    {
      id: 3,
      PromotedEmployee: "Michael Chen",
      EmpNo: "EMP00789",
      DesignationFrom: "Accountant",
      DesignationTo: "Senior Accountant",
      Department: "Finance",
      PromotionDate: "2025-01-25",
      remarks: "CPA certification completed",
    },
    {
      id: 4,
      PromotedEmployee: "Lisa Rodriguez",
      EmpNo: "EMP00987",
      DesignationFrom: "HR Associate",
      DesignationTo: "HR Specialist",
      Department: "Human Resources",
      PromotionDate: "2025-02-01",
      remarks: "Improved employee retention by 15%",
    },
    {
      id: 5,
      PromotedEmployee: "David Wilson",
      EmpNo: "EMP00321",
      DesignationFrom: "Sales Representative",
      DesignationTo: "Sales Manager",
      Department: "Sales",
      PromotionDate: "2025-02-05",
      remarks: "Exceeded quarterly targets by 25%",
    },
  ]);

  // Filter states for cycles
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [gradeFilter, setGradeFilter] = useState<string>("All");

  // States for employee list
  const [activeTab, setActiveTab] = useState<number>(0);
  const [query, setQuery] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // JSON modal state
  const [showJsonModal, setShowJsonModal] = useState<boolean>(false);
  const [jsonDraft, setJsonDraft] = useState<string>("");

  const tabs = ["APAR Shortfall", "Promoted Employees", "Exceptions"];
  const PAGE_SIZES = [5, 10, 25, 50, 100];

  /* ---------- DERIVED VALUES ---------- */
  const filteredCycles = useMemo(() => {
    return cycles.filter(
      c =>
        (statusFilter === "All" || c.status === statusFilter) &&
        (gradeFilter === "All" || c.grade === gradeFilter)
    );
  }, [cycles, statusFilter, gradeFilter]);

  const dashboardStats = useMemo(() => ({
    active: cycles.filter(c => c.status === "Active").length,
    totalEligible: cycles.reduce((s, c) => s + c.eligible, 0),
    dpcPending: cycles.filter(c => c.status === "DPC Review").length,
    approved: cycles.filter(c => c.status === "Approved").length,
    sapSynced: cycles.filter(c => c.status === "SAP Synced").length,
  }), [cycles]);

  // Filter and paginate promotion items
  const filteredItems = useMemo(() => {
    let list = promotionItems;

    // Simple filter logic for tabs (mock implementation)
    if (activeTab === 0) { // APAR Shortfall
      list = promotionItems.filter(item => item.id && Number(item.id) % 2 === 0);
    } else if (activeTab === 2) { // Exceptions
      list = promotionItems.filter(item => item.id && Number(item.id) % 3 === 0);
    }
    // Tab 1 "Promoted Employees" shows all for now

    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter(item =>
      item.PromotedEmployee.toLowerCase().includes(q) ||
      item.EmpNo?.toLowerCase().includes(q) ||
      item.Department?.toLowerCase().includes(q) ||
      item.DesignationFrom?.toLowerCase().includes(q)
    );
  }, [promotionItems, query, activeTab]);

  const total = filteredItems.length;
  const totalPages = Math.ceil(total / pageSize);
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredItems.slice(start, end);
  }, [filteredItems, page, pageSize]);

  /* ---------- HELPER FUNCTIONS ---------- */
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Active": return "badge bg-info";
      case "DPC Review": return "badge bg-warning";
      case "Approved": return "badge bg-success";
      case "SAP Synced": return "badge bg-purple";
      case "Draft": return "badge bg-secondary";
      default: return "badge bg-light text-dark border";
    }
  };

  /* ---------- CONFIGURATIONS ---------- */
  const dashboardSections = [
    {
      title: "Overview",
      tiles: [
        { title: "Active Cycles", icon: "ti-activity", count: dashboardStats.active, link: "#", colorClass: "text-info" },
        { title: "Total Eligible", icon: "ti-users", count: dashboardStats.totalEligible, link: "#", colorClass: "text-success" },
        { title: "DPC Review", icon: "ti-clock", count: dashboardStats.dpcPending, link: "#", colorClass: "text-warning" },
        { title: "Approved", icon: "ti-check", count: dashboardStats.approved, link: "#", colorClass: "text-primary" },
        { title: "SAP Synced", icon: "ti-refresh", count: dashboardStats.sapSynced, link: "#", colorClass: "text-purple" },
      ]
    },
  ];

  const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const cycleColumns: TableColumn<PromotionCycle>[] = [
    { title: "Cycle ID", key: "id", render: (val: string) => <span className="fw-medium text-dark">{val}</span> },
    { title: "Name", key: "name" },
    { title: "Grade", key: "grade", className: "text-center", render: (val: string) => <span className="badge bg-light text-dark border">{val}</span> },
    { title: "Cadre", key: "cadré", className: "text-center" },
    { title: "Promotion Due On", key: "baseDate", className: "text-center" },
    { title: "Eligible", key: "eligible", className: "text-center", render: (val: number) => <span className="fw-bold text-success">{val}</span> },
    ...["A", "B", "C", "D", "E"].map(l => ({
      title: `L${l}`,
      key: `lists.${l}`,
      className: "text-center",
      render: (_: any, record: PromotionCycle) => {
        const val = record.lists[l as keyof typeof record.lists];
        return <span className={`badge ${val > 0 ? 'bg-warning-light text-warning' : 'bg-light text-muted'}`}>{val}</span>;
      }
    })),
    { title: "Status", key: "status", className: "text-center", render: (val: string) => <span className={getStatusBadgeClass(val)}>{val}</span> },
    {
      title: "Action",
      key: "action",
      className: "text-end",
      render: (_: any, record: PromotionCycle) => (
        <div className="d-flex justify-content-end gap-2">
          <Link to={`${all_routes.PostPromotion.list}?cycleId=${record.id}`} className="btn btn-icon btn-sm btn-outline-primary" title="View List">
            <i className="ti ti-eye"></i>
          </Link>
          <Link to="#" className="btn btn-icon btn-sm btn-outline-danger" title="Delete">
            <i className="ti ti-trash"></i>
          </Link>
        </div>
      )
    },
  ];

  const employeeColumns: TableColumn<PromotionItem>[] = [
    { title: "Sr. No", key: "sr", render: (_: any, __: any, idx: number) => (page - 1) * pageSize + idx + 1 },
    { title: "Emp No", key: "EmpNo" },
    {
      title: "Name",
      key: "PromotedEmployee",
      render: (_: any, record: PromotionItem) => (
        <>
          <div className="fw-medium">{record.PromotedEmployee}</div>
          <small className="text-muted">{record.Department}</small>
        </>
      )
    },
    {
      title: "Designation",
      key: "designation",
      render: (_: any, record: PromotionItem) => (
        <>{record.DesignationFrom} <i className="ti ti-arrow-right mx-1 text-muted"></i> {record.DesignationTo}</>
      )
    },
    { title: "Promotion Date", key: "PromotionDate" },
    { title: "Remarks", key: "remarks", className: "text-muted" },
    // {
    //   title: "Action",
    //   key: "action",
    //   className: "text-end",
    //   render: (_: any, record: PromotionItem) => (
    //     <div className="d-flex justify-content-end gap-2">
    //       <button className="btn btn-icon btn-sm btn-outline-primary" title="Edit"><i className="ti ti-edit"></i></button>
    //       <button className="btn btn-icon btn-sm btn-outline-danger" title="Delete"><i className="ti ti-trash"></i></button>
    //     </div>
    //   )
    // },
  ];

  /* ================= RENDER ================= */
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* HEADER */}
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title text-dark fw-bold">Post Promotional Dashboard</h3>
              <ul className="breadcrumb bg-transparent p-0 mt-1">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Post-Promotional</li>
              </ul>
            </div>
            <div className="col-auto float-end ms-auto">
              <Link to={all_routes.PostPromotion.newRegistration} className="btn btn-primary">
                <i className="ti ti-plus me-1"></i> New Cycle
              </Link>
            </div>
          </div>
        </div>

        {/* DASHBOARD SECTIONS */}
        {dashboardSections.map((section, sIdx) => (
          <DashboardSection key={sIdx} title={section.title}>
            {section.tiles.map((tile, tIdx) => (
              <DashboardTile
                key={tIdx}
                title={tile.title}
                icon={tile.icon}
                count={tile.count}
                link={tile.link}
                colorClass={tile.colorClass}
              />
            ))}
          </DashboardSection>
        ))}

        {/* PROMOTION CYCLES SECTION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '12px' }}>
          <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '12px 12px 0 0' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0">Promotion Cycles</h5>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={gradeFilter}
                  onChange={(e) => setGradeFilter(e.target.value)}
                >
                  <option value="All">All Grades</option>
                  {Array.from(new Set(cycles.map(c => c.grade))).map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All Status</option>
                  {Array.from(new Set(cycles.map(c => c.status))).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setJsonDraft(JSON.stringify(cycles, null, 2));
                    setShowJsonModal(true);
                  }}
                  className="btn btn-sm btn-light border"
                >
                  JSON
                </button>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover table-center mb-0">
              <thead className="thead-light">
                <tr>
                  {cycleColumns.map((col, idx) => (
                    <th key={idx} className={col.className || ""}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCycles.map((cycle, idx) => (
                  <tr key={cycle.id}>
                    {cycleColumns.map((col, cIdx) => (
                      <td key={cIdx} className={col.className || ""}>
                        {col.render ? col.render(getValueByPath(cycle, col.key), cycle, idx) : getValueByPath(cycle, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
                {filteredCycles.length === 0 && (
                  <tr><td colSpan={cycleColumns.length} className="text-center py-4 text-muted">No promotion cycles found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* EMPLOYEE PROMOTION LIST SECTION */}
        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
          <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '12px 12px 0 0' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <div className="btn-group" role="group">
                {tabs.map((tab, index) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(index)}
                    className={`btn btn-sm ${index === activeTab ? "btn-primary" : "btn-light border"}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="d-flex gap-2 w-100 w-md-auto">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search employees..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <select className="form-select form-select-sm w-auto" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover table-center mb-0">
              <thead className="thead-light">
                <tr>
                  {employeeColumns.map((col, idx) => (
                    <th key={idx} className={col.className || ""}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageData.map((item, idx) => (
                  <tr key={idx}>
                    {employeeColumns.map((col, cIdx) => (
                      <td key={cIdx} className={col.className || ""}>
                        {col.render ? col.render(getValueByPath(item, col.key), item, idx) : getValueByPath(item, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
                {pageData.length === 0 && (
                  <tr><td colSpan={employeeColumns.length} className="text-center py-4 text-muted">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="card-footer bg-white border-top-0 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} entries</small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button>
                  </li>
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        {/* JSON MODAL */}
        {showJsonModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit JSON Data</h5>
                  <button type="button" className="btn-close" onClick={() => setShowJsonModal(false)}></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control font-monospace"
                    rows={15}
                    value={jsonDraft}
                    onChange={e => setJsonDraft(e.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowJsonModal(false)}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={() => {
                    try {
                      const parsed = JSON.parse(jsonDraft);
                      setCycles(parsed);
                      setShowJsonModal(false);
                    } catch (error) {
                      alert("Invalid JSON format");
                    }
                  }}>Apply Changes</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
        .btn-icon {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 4px;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .text-purple { color: #6f42c1 !important; }
        .bg-purple { background-color: #6f42c1 !important; color: white; }
        .bg-warning-light { background-color: rgba(255, 193, 7, 0.1); }
      `}</style>
      </div>
    </div>
  );
};

export default PostPromotion;